import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { useProfile } from './ProfileContext';
import { addDays, differenceInDays, parseISO } from 'date-fns';

interface CycleLog {
  id: number;
  user_id: string;
  date: string;
  period_start: boolean;
  flow_level: string | null;
  symptoms: string[] | null;
  mood: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface CycleStats {
  averageCycleLength: number;
  lastPeriodDate: string | null;
  nextPeriodPrediction: string | null;
  currentPhase: string;
  phaseDay: number;
}

interface CycleContextType {
  logs: CycleLog[];
  stats: CycleStats;
  loading: boolean;
  error: string | null;
  fetchLogs: () => Promise<void>;
  addLog: (log: Omit<CycleLog, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateLog: (id: number, log: Partial<Omit<CycleLog, 'id' | 'user_id' | 'created_at' | 'updated_at'>>) => Promise<void>;
  deleteLog: (id: number) => Promise<void>;
}

const defaultStats: CycleStats = {
  averageCycleLength: 28,
  lastPeriodDate: null,
  nextPeriodPrediction: null,
  currentPhase: 'follicular',
  phaseDay: 1,
};

const CycleContext = createContext<CycleContextType | undefined>(undefined);

export const useCycle = () => {
  const context = useContext(CycleContext);
  if (context === undefined) {
    throw new Error('useCycle must be used within a CycleProvider');
  }
  return context;
};

interface CycleProviderProps {
  children: ReactNode;
}

export const CycleProvider = ({ children }: CycleProviderProps) => {
  const [logs, setLogs] = useState<CycleLog[]>([]);
  const [stats, setStats] = useState<CycleStats>(defaultStats);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, isAuthenticated } = useAuth();
  const { preferences } = useProfile();

  const calculateStats = (cycleLogs: CycleLog[]): CycleStats => {
    if (cycleLogs.length === 0) return defaultStats;

    const periodStarts = cycleLogs
      .filter(log => log.period_start)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    if (periodStarts.length === 0) return defaultStats;

    const lastPeriodDate = periodStarts[periodStarts.length - 1].date;
    
    // Use user preferences for cycle length if available, otherwise calculate from data
    let averageCycleLength = preferences?.cycle_length_average || 28;
    
    if (periodStarts.length > 1) {
      const cycleLengths = [];
      for (let i = 1; i < periodStarts.length; i++) {
        const length = differenceInDays(
          parseISO(periodStarts[i].date),
          parseISO(periodStarts[i - 1].date)
        );
        // Only include realistic cycle lengths
        if (length >= 21 && length <= 35) {
          cycleLengths.push(length);
        }
      }
      
      if (cycleLengths.length > 0) {
        const calculatedAverage = Math.round(
          cycleLengths.reduce((sum, length) => sum + length, 0) / cycleLengths.length
        );
        
        // If user has set preferences, use a weighted average (70% user preference, 30% calculated)
        if (preferences?.cycle_length_average) {
          averageCycleLength = Math.round(
            (preferences.cycle_length_average * 0.7) + (calculatedAverage * 0.3)
          );
        } else {
          averageCycleLength = calculatedAverage;
        }
      }
    }

    // Predict next period using user preferences or calculated average
    const nextPeriodPrediction = addDays(parseISO(lastPeriodDate), averageCycleLength).toISOString().split('T')[0];

    // Calculate current phase and day
    const daysSinceLastPeriod = differenceInDays(new Date(), parseISO(lastPeriodDate));
    let currentPhase = 'follicular';
    let phaseDay = daysSinceLastPeriod + 1;

    // Use user's period length preference for menstrual phase calculation
    const periodLength = preferences?.period_length_average || 5;
    
    if (daysSinceLastPeriod <= periodLength - 1) {
      currentPhase = 'menstrual';
    } else if (daysSinceLastPeriod <= Math.round(averageCycleLength * 0.5) - 2) {
      currentPhase = 'follicular';
    } else if (daysSinceLastPeriod <= Math.round(averageCycleLength * 0.5) + 1) {
      currentPhase = 'ovulation';
    } else {
      currentPhase = 'luteal';
    }

    return {
      averageCycleLength,
      lastPeriodDate,
      nextPeriodPrediction,
      currentPhase,
      phaseDay,
    };
  };

  const fetchLogs = async () => {
    if (!isAuthenticated || !user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from('cycle_logs')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false });

      if (fetchError) throw fetchError;

      setLogs(data || []);
      setStats(calculateStats(data || []));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch cycle logs');
    } finally {
      setLoading(false);
    }
  };

  // Recalculate stats when preferences change
  useEffect(() => {
    if (logs.length > 0) {
      setStats(calculateStats(logs));
    }
  }, [preferences, logs]);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchLogs();
    }
  }, [isAuthenticated, user]);

  const addLog = async (log: Omit<CycleLog, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => {
    if (!user) throw new Error('User not authenticated');

    try {
      setLoading(true);
      setError(null);
      
      const { data, error: insertError } = await supabase
        .from('cycle_logs')
        .insert([{ ...log, user_id: user.id }])
        .select()
        .single();

      if (insertError) throw insertError;

      const updatedLogs = [data, ...logs];
      setLogs(updatedLogs);
      setStats(calculateStats(updatedLogs));
    } catch (err: any) {
      setError(err.message || 'Failed to add cycle log');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateLog = async (id: number, log: Partial<Omit<CycleLog, 'id' | 'user_id' | 'created_at' | 'updated_at'>>) => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error: updateError } = await supabase
        .from('cycle_logs')
        .update({ ...log, updated_at: new Date().toISOString() })
        .eq('id', id)
        .eq('user_id', user?.id)
        .select()
        .single();

      if (updateError) throw updateError;

      const updatedLogs = logs.map(l => l.id === id ? data : l);
      setLogs(updatedLogs);
      setStats(calculateStats(updatedLogs));
    } catch (err: any) {
      setError(err.message || 'Failed to update cycle log');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteLog = async (id: number) => {
    try {
      setLoading(true);
      setError(null);
      
      const { error: deleteError } = await supabase
        .from('cycle_logs')
        .delete()
        .eq('id', id)
        .eq('user_id', user?.id);

      if (deleteError) throw deleteError;

      const updatedLogs = logs.filter(l => l.id !== id);
      setLogs(updatedLogs);
      setStats(calculateStats(updatedLogs));
    } catch (err: any) {
      setError(err.message || 'Failed to delete cycle log');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <CycleContext.Provider
      value={{
        logs,
        stats,
        loading,
        error,
        fetchLogs,
        addLog,
        updateLog,
        deleteLog,
      }}
    >
      {children}
    </CycleContext.Provider>
  );
};